import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PostSummary,
  listPostsSummary,
  updatePostStatus,
} from '../api/posts';
import { listSchoolsSummary } from '../api/schools';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

export const PostsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<PostSummary[]>({
    queryKey: ['posts'],
    queryFn: listPostsSummary,
  });
  const { data: schools } = useQuery({
    queryKey: ['schools-summary'],
    queryFn: listSchoolsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'title' | 'school' | 'status' | 'date' | 'createdAt' | 'updatedAt'
  >('date');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingPost, setPendingPost] = React.useState<{
    ids: string[];
    status: string;
  } | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const confirmOpen = Boolean(confirmAnchorEl);

  const schoolNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    schools?.forEach((s) => {
      map[s.id] = s.name || s.id;
    });
    return map;
  }, [schools]);

  const handleRequestSort = (
    property:
      | 'title'
      | 'school'
      | 'status'
      | 'date'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((p) => {
      const schoolName = p.schoolId
        ? schoolNameById[p.schoolId] ?? p.schoolId
        : '';
      const haystack = `${p.title ?? ''} ${schoolName}`.toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (p.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, schoolNameById]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | undefined;
      let bv: string | Date | undefined;

      const getSchoolName = (p: PostSummary) =>
        p.schoolId ? schoolNameById[p.schoolId] ?? p.schoolId : '';

      switch (orderBy) {
        case 'title':
          av = (a.title ?? '').toLowerCase();
          bv = (b.title ?? '').toLowerCase();
          break;
        case 'school':
          av = getSchoolName(a).toLowerCase();
          bv = getSchoolName(b).toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'date':
          av = a.date ?? new Date(0);
          bv = b.date ?? new Date(0);
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, schoolNameById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangeStatusRequest = (
    event: React.MouseEvent<HTMLElement>,
    idsOrPost: PostSummary | string[],
    status: string
  ) => {
    const actionLabel = status === 'approved' ? 'approuver' : 'rejeter';

    if (Array.isArray(idsOrPost)) {
      const ids = idsOrPost;
      if (!ids.length) return;
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} les ${ids.length} postes sélectionnés ?`
      );
      setPendingPost({ ids, status });
    } else {
      const post = idsOrPost;
      const schoolName = post.schoolId
        ? schoolNameById[post.schoolId] ?? post.schoolId
        : 'cette école';
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} le poste "${post.title ?? 'Sans titre'}" pour ${schoolName} ?`
      );
      setPendingPost({ ids: [post.id], status });
    }

    setConfirmAnchorEl(event.currentTarget);
  };

  const handleConfirmPost = async () => {
    if (!pendingPost) return;
    await Promise.all(
      pendingPost.ids.map((id) => updatePostStatus(id, pendingPost.status))
    );
    await queryClient.invalidateQueries({ queryKey: ['posts'] });
    setConfirmAnchorEl(null);
    setPendingPost(null);
    setSelectedIds([]);
  };

  const handleCancelPost = () => {
    setConfirmAnchorEl(null);
    setPendingPost(null);
  };

  const allSelected =
    filtered.length > 0 &&
    filtered.every((p) => selectedIds.includes(p.id));
  const someSelected =
    filtered.length > 0 &&
    filtered.some((p) => selectedIds.includes(p.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Postes
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Titre, école..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="approved">Approuvé</MenuItem>
            <MenuItem value="rejected">Rejeté</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length
            ? `${selectedIds.length} sélectionné(s)`
            : 'Aucune sélection'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'approved')
            }
          >
            Approuver la sélection
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'rejected')
            }
          >
            Rejeter la sélection
          </Button>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sortDirection={orderBy === 'title' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleRequestSort('title')}
                >
                  Titre
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'school' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'school'}
                  direction={orderBy === 'school' ? order : 'asc'}
                  onClick={() => handleRequestSort('school')}
                >
                  École
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((p) => (
                <TableRow key={p.id} hover selected={selectedIds.includes(p.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(p.id)}
                      onChange={() => toggleOne(p.id)}
                    />
                  </TableCell>
                  <TableCell>{p.title ?? 'Sans titre'}</TableCell>
                  <TableCell>
                    {p.schoolId
                      ? schoolNameById[p.schoolId] ?? p.schoolId
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.status ?? 'pending'}
                      color={statusColor(p.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(p.date)}</TableCell>
                  <TableCell>{formatDate(p.createdAt)}</TableCell>
                  <TableCell>{formatDate(p.updatedAt)}</TableCell>
                  <TableCell align="right">
                    {p.status !== 'approved' && (
                      <Tooltip title="Approuver le poste">
                        <IconButton
                          size="small"
                        onClick={(event) =>
                          handleChangeStatusRequest(event, p, 'approved')
                        }
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {p.status !== 'rejected' && (
                      <Tooltip title="Rejeter le poste">
                        <IconButton
                          size="small"
                        onClick={(event) =>
                          handleChangeStatusRequest(event, p, 'rejected')
                        }
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun poste trouvé.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
      <Popover
        open={confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelPost}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Confirmer l&apos;action
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelPost} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmPost}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
};

